import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import PageHeader from '@/components/PageHeader';
import OrgSubscriptionPanel from '@/components/billing/OrgSubscriptionPanel';
import { organizationsService, type OrgDashboard } from '@/api/services/organizations';
import { usageService, type OrgUsageSummary } from '@/api/services/usage';
import { subscriptionService, type SubscriptionStatus } from '@/api/services/notifications';
import { useWorkspace } from '@/context/WorkspaceContext';
import { useMe } from '@/context/MeContext';
import { canViewWorkspaceUsers } from '@/utils/navCapabilities';
import { formatApiError } from '@/utils/formatApiError';
import { formatPlanDisplayName } from '@/utils/planDisplayName';
import { LIFECYCLE_STATE_LABELS } from '@/utils/lifecycleReasons';

export default function OrganizationPage() {
  const location = useLocation();
  const { me } = useMe();
  const { workspaceVersion, isPersonalMode, canManageOrganizations } = useWorkspace();
  const showUsersLink = canViewWorkspaceUsers(me);
  const canManageSubscription =
    (me?.permissions.canManageOrgSubscription ?? false) ||
    (me?.permissions.canUseBillingCheckout ?? false);
  const canManageSettings = me?.permissions.canManageWorkspaceMembers ?? false;
  const [data, setData] = useState<OrgDashboard | null>(null);
  const [usage, setUsage] = useState<OrgUsageSummary | null>(null);
  const [subscription, setSubscription] = useState<SubscriptionStatus | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadBilling = useCallback(async () => {
    try {
      const [u, sub] = await Promise.all([
        usageService.summary('current_month'),
        subscriptionService.status().catch(() => null),
      ]);
      setUsage(u);
      setSubscription(sub);
    } catch {
      /* billing optional on org overview */
    }
  }, []);

  const loadDashboard = useCallback(async () => {
    try {
      const dashboard = await organizationsService.dashboard();
      setData(dashboard);
      setError(null);
    } catch (e) {
      setError(formatApiError(e));
      setData(null);
    }
  }, []);

  useEffect(() => {
    if (isPersonalMode) {
      setData(null);
      setUsage(null);
      setSubscription(null);
      setError(null);
      return;
    }
    void loadDashboard();
    void loadBilling();
  }, [isPersonalMode, loadDashboard, loadBilling, workspaceVersion, location.key, location.state]);

  if (isPersonalMode) {
    return (
      <div>
        <PageHeader
          title="Workspace"
          subtitle="Switch to an organization workspace to view team overview, members, and billing."
        />
        <section className="panel">
          <p>
            You are in <strong>Personal</strong> mode. Personal workspace is for your private searches,
            recipients, and templates.
          </p>
          <p className="muted">
            To manage a customer organization, choose it from the workspace switcher in the sidebar.
          </p>
          {canManageOrganizations ? (
            <div className="table-action-bar">
              <Link className="btn btn-primary btn-sm" to="/organizations/manage">
                Manage organizations
              </Link>
            </div>
          ) : null}
        </section>
      </div>
    );
  }

  if (error) return <div className="panel alert alert-error">{error}</div>;
  if (!data) return <div className="panel">Loading workspace…</div>;

  const health = (data.health?.health as string) || 'unknown';
  const planLabel = usage?.unlimited
    ? 'Internal (Unlimited)'
    : formatPlanDisplayName(usage?.planSlug);
  const summary = data.memberSummary;

  return (
    <div>
      <PageHeader
        title={data.organization?.name ?? 'My workspace'}
        subtitle="Team overview, members, and plan usage."
      />
      {summary ? (
        <section className="panel usage-summary-grid">
          <h2>Members</h2>
          <div className="usage-summary-grid">
            <p>
              Members: <strong>{summary.total}</strong>
            </p>
            <p>
              Workspace admins: <strong>{summary.workspaceAdmins}</strong>
            </p>
            <p>
              Editors: <strong>{summary.editors}</strong>
            </p>
            <p>
              Viewers: <strong>{summary.viewers}</strong>
            </p>
            <p className="muted">
              Invitations: <strong>{summary.pendingInvitations}</strong> pending
            </p>
          </div>
        </section>
      ) : null}
      <div className="usage-summary-grid panel">
        <p>
          Health: <strong>{health}</strong> · Members: {data.members.length} · Searches:{' '}
          {data.searchCount} · Recipients: {data.recipientCount}
        </p>
        <p className="muted">Pending invitations: {data.pendingInvitations.length}</p>
        <div className="table-action-bar">
          {showUsersLink ? (
            <Link className="btn btn-sm" to="/users">
              Manage users
            </Link>
          ) : null}
          {canManageSettings ? (
            <Link className="btn btn-sm" to="/workspace/settings">
              Settings
            </Link>
          ) : null}
          <Link className="btn btn-sm" to="/usage">
            Usage &amp; billing
          </Link>
        </div>
      </div>

      {usage ? (
        <section className="panel org-billing-overview" aria-label="Plan and usage">
          <div className="usage-summary-grid">
            <p>
              Plan: <strong>{planLabel}</strong>
            </p>
            {subscription ? (
              <p className="muted">
                {LIFECYCLE_STATE_LABELS[subscription.lifecycleState] ?? subscription.lifecycleLabel}
                {subscription.expiresAt
                  ? ` · expires ${new Date(subscription.expiresAt).toLocaleDateString()}`
                  : ''}
              </p>
            ) : usage.expiresAt ? (
              <p className="muted">Expires {new Date(usage.expiresAt).toLocaleDateString()}</p>
            ) : null}
            <p className="muted">
              Notifications today: {usage.usage.notificationsSentToday}
              {usage.limits.maxNotificationsPerDay != null
                ? ` / ${usage.limits.maxNotificationsPerDay}`
                : ' / Unlimited'}
            </p>
            <p className="muted">
              Active searches: {usage.usage.activeSearches}
              {usage.limits.maxSearches != null ? ` / ${usage.limits.maxSearches}` : ' / Unlimited'}
            </p>
          </div>
          <div className="table-action-bar">
            {canManageSubscription ? (
              <Link className="btn btn-sm btn-primary" to="/usage#manage-subscription">
                Manage subscription
              </Link>
            ) : null}
            <Link className="btn btn-sm" to="/usage">
              Full usage &amp; billing
            </Link>
          </div>
        </section>
      ) : null}

      {usage ? (
        <OrgSubscriptionPanel usage={usage} subscription={subscription} onChanged={loadBilling} compact />
      ) : null}
    </div>
  );
}
